import { Container, useMediaQuery } from '@ag1/ui';
import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import StudioResponsiveMedia from 'shared/studio/StudioResponsiveMedia/StudioResponsiveMedia';
import { useVideoAutoplayOnView } from 'shared/studio/StudioResponsiveMedia/useVideoAutoplayOnView';
import UnifiedButton from 'shared/studio/UnifiedButton/UnifiedButton';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import { LargeVideo as LargeVideoProps, SanityKeyed } from 'types/studio/studio';
import { CommonSectionProps } from '../types';
import styles from './LargeVideo.module.scss';
import LargeVideoHeading from './LargeVideoHeading/LargeVideoHeading';
import { VideoPlayer } from './VideoPlayer';

const LargeVideo = ({
  _key: key,
  headline,
  subtitle,
  media,
  padding,
  textColor,
  fullWidthVideo,
  primaryButton,
  secondaryButton,
  contentAlignment = 'left',
  mobileContentAlignment = 'left',
  mobileButtonPlacement = 'aboveVideo',
  videoPlayerButtonTheme = 'light',
  autoplay = false,
  autoplayOnVisibleThreshold = 0.25,
  muted = false,
  autoplayOnlyOnce = false
}: SanityKeyed<LargeVideoProps> & CommonSectionProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isVideoMounted, setIsVideoMounted] = useState(false);
  const aboveSmall = useMediaQuery('s');
  const aspectRatio = aboveSmall ? 2 / 1 : 4 / 5;

  const handleVideoMounted = useCallback(() => {
    setIsVideoMounted(true);
  }, []);

  useVideoAutoplayOnView({
    videoRef,
    shouldAutoplay: autoplay && isVideoMounted,
    shouldPauseOnExit: autoplay && isVideoMounted,
    threshold: autoplayOnVisibleThreshold,
    rootMargin: '20px',
    autoplayOnlyOnce
  });

  const hasButtons = primaryButton || secondaryButton;

  const Buttons = hasButtons ? (
    <div className={styles.buttonsContainer}>
      {primaryButton && (
        <UnifiedButton
          unifiedButton={{ ...primaryButton, _type: 'unifiedButton' }}
          sectionType="largeVideo"
          size="medium"
          className={styles.button}
        />
      )}
      {secondaryButton && (
        <UnifiedButton
          unifiedButton={{ ...secondaryButton, _type: 'unifiedButton' }}
          sectionType="largeVideo"
          size="medium"
          className={styles.button}
        />
      )}
    </div>
  ) : null;

  const showMobileButtonsBelow = !aboveSmall && mobileButtonPlacement === 'belowVideo';
  const rootContainerPaddingClass = clsx(
    styles.defaultPadding,
    getSectionPaddingClasses(styles, padding)
  );

  return (
    <div
      className={clsx(rootContainerPaddingClass, {
        [styles.fullWidthRoot]: fullWidthVideo
      })}>
      <Container narrow id={key}>
        <article className={clsx(styles.root)}>
          {(headline || subtitle || Buttons) && (
            <div
              className={clsx(styles.topSectionContainer, {
                [styles.left]: contentAlignment === 'left',
                [styles.center]: contentAlignment === 'center'
              })}>
              <LargeVideoHeading
                headline={headline}
                subtitle={subtitle}
                textColor={textColor}
                contentAlignment={contentAlignment}
                mobileContentAlignment={mobileContentAlignment}
              />
              {!showMobileButtonsBelow && Buttons}
            </div>
          )}

          {!fullWidthVideo && (
            <StudioResponsiveMedia
              {...media}
              aspectRatio={aspectRatio}
              eagerLoading={false}
              className={clsx(styles.videoPlayerGrid)}
              autoPlay={false}
              muted={muted}
              videoRef={videoRef}
              showImageAsVideoThumbnail
              onVideoMounted={handleVideoMounted}
              customVideoOverlay={
                <VideoPlayer
                  videoRef={videoRef}
                  theme={videoPlayerButtonTheme}
                  initialIsMuted={muted}
                />
              }
            />
          )}
          {showMobileButtonsBelow && !fullWidthVideo && Buttons}
        </article>
      </Container>

      {/* Full width video is outside of the Container to allow for full width */}
      {fullWidthVideo && (
        <StudioResponsiveMedia
          {...media}
          aspectRatio={aspectRatio}
          eagerLoading={false}
          className={clsx(styles.videoPlayerFullWidth)}
          autoPlay={false}
          muted={muted}
          videoRef={videoRef}
          showImageAsVideoThumbnail
          onVideoMounted={handleVideoMounted}
          customVideoOverlay={
            <VideoPlayer
              videoRef={videoRef}
              theme={videoPlayerButtonTheme}
              initialIsMuted={muted}
            />
          }
        />
      )}

      {/* If fullWidthVideo is true and buttons are shown below, use a separate Container to get default padding etc for the buttons */}
      {showMobileButtonsBelow && fullWidthVideo && (
        <Container narrow>
          <article>{Buttons}</article>
        </Container>
      )}
    </div>
  );
};

export default LargeVideo;
